import { ElementRef, InputHTMLAttributes, forwardRef, useState } from 'react';

import { cva } from 'class-variance-authority';
import { SearchIcon } from 'ui/components/icon';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';

export interface PROPS
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  type?: 'text' | 'password' | 'email' | 'number';
  size?: SIZE | 'default';
}

const inputClasses = cva(
  [
    'flex-1',
    'bg-transparent',
    'text-sm',
    'pr-2.5 pl-8',
    'focus:outline-0 focus:border-0 focus-visible:outline-none',
    'disabled:placeholder-disabled disabled:text-disabled',
  ],
  {
    variants: {
      size: {
        default: ['w-full'],
        [SIZE.Small]: [],
        [SIZE.Medium]: [],
        [SIZE.Large]: ['text-[16px]'],
      },
    },
    defaultVariants: { size: 'default' },
  },
);

const iconClasses = cva(
  [
    'h-4 w-4',
    'absolute',
    'left-2.5 top-1/2 -translate-y-1/2',
    'hover:text-fill-quarternary',
  ],
  {
    variants: {
      isFocused: {
        true: ['text-secondary-dark'],
        false: ['text-content'],
      },
      disabled: {
        true: ['!text-disabled'],
        false: [],
      },
    },
    defaultVariants: { isFocused: false, disabled: false },
  },
);

const containerClasses = cva(
  ['border rounded-[6px] py-[7px]', 'flex items-center', 'bg-fill-secondary'],
  {
    variants: {
      isFocused: {
        true: ['border-primary-base'],
        false: ['border-separator-secondary'],
      },
      size: {
        default: [],
        [SIZE.Small]: ['py-2', 'px-2.5', 'h-[36px]'],
        [SIZE.Medium]: ['px-2.5', 'py-3', 'h-[44px]'],
        [SIZE.Large]: ['px-2.5', 'py-4', 'h-[52px]'],
      },
    },
    defaultVariants: { isFocused: false, size: SIZE.Small },
  },
);

const Searchbox = forwardRef<ElementRef<'input'>, PROPS>(
  ({ size, disabled, className, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);

    return (
      <div
        className={cn(
          'relative',
          containerClasses({ isFocused, size }),
          className,
        )}
      >
        <SearchIcon className={iconClasses({ isFocused, disabled })} />
        <input
          ref={ref}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...props}
          type="text"
          className={cn(inputClasses({ size }))}
          disabled={disabled}
        />
      </div>
    );
  },
);

export default Searchbox;
